import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth } from '../config/firebase';
import { trackError, ERROR_CONTEXTS } from './analytics.service';

// Explicitly specify the region where the function is deployed
const functions = getFunctions(app, 'us-central1');

/**
 * Calls the generatePattern Cloud Function
 * Can be used for both purchase flow (with sessionId) and subscribed user flow (without sessionId)
 *
 * @param {string} patternId - Pattern document ID
 * @param {string} sessionId - (optional) Stripe checkout session ID
 *   - If provided: pattern will be generated after payment verification
 *   - If not provided: pattern will be generated for subscribed users
 *
 * @returns {Promise<object>} { downloadURL: string }
 * @throws {Error} With specific error codes:
 *   - 'not-yet-paid': Payment webhook not yet processed (retry with backoff)
 *   - 'permission-denied': User not authorized
 *   - 'not-found': Pending pattern document not found
 *   - 'internal': PDF generation or upload failed
 */
export const callGeneratePattern = async (patternId, sessionId = null) => {
	// Ensure user is authenticated
	const currentUser = auth.currentUser;
	if (!currentUser) {
		throw new Error('User must be authenticated to call this function');
	}

	// Refresh the auth token to ensure it's valid (important after Stripe redirect)
	try {
		await currentUser.getIdToken(true); // Force token refresh
	} catch (err) {
		trackError(ERROR_CONTEXTS.TOKEN_REFRESH, err);
		throw new Error('Failed to refresh authentication token');
	}

	const generatePatternFn = httpsCallable(functions, 'generatePattern');

	const payload = { patternId };
	if (sessionId) {
		payload.sessionId = sessionId;
	}

	try {
		const result = await generatePatternFn(payload);
		return result.data;
	} catch (error) {
		trackError(ERROR_CONTEXTS.CALL_GENERATE_PATTERN, error, { patternId });
		throw error;
	}
};
